/**
 * AvatarRing — web port of `apps/WunderGuide/components/AvatarRing.tsx`.
 *
 * Round avatar with a status ring that reflects voice-credit state
 * (good / low / critical / exhausted) — same behaviour as RN:
 *
 *   • good: white static ring + soft glow
 *   • low: amber static ring + subtle pulse (1.014 scale, 2s cycle)
 *   • critical: red countdown ring depleting clockwise to empty (60s max)
 *               + faster pulse (1s cycle)
 *   • exhausted: red attention pulse (scale 1 → 1.08 / 800ms infinite)
 *
 * Tap fires `onPress` (Dashboard wires this to `openDrawer('settings')` /
 * paywall trigger).
 */
import React, { useEffect, useMemo } from 'react';
import { getRingState, RingState, TimeAllowance } from '../types/allowance';
import './AvatarRing.css';

interface RingConfig {
  color: string;
  glowColor: string;
  glowSize: number;
  strokeWidth: number;
}

const RING_CONFIG: Record<RingState, RingConfig> = {
  good: {
    color: 'rgba(255, 255, 255, 0.5)',
    glowColor: 'rgba(255, 255, 255, 0.3)',
    glowSize: 4,
    strokeWidth: 3,
  },
  low: {
    color: '#FFB800',
    glowColor: 'rgba(255, 184, 0, 0.5)',
    glowSize: 4,
    strokeWidth: 3,
  },
  critical: {
    color: '#FF3B30',
    glowColor: 'rgba(255, 59, 48, 0.5)',
    glowSize: 4,
    strokeWidth: 3,
  },
  exhausted: {
    color: '#FF3B30',
    glowColor: 'rgba(255, 59, 48, 0.4)',
    glowSize: 5,
    strokeWidth: 3,
  },
};

interface AvatarRingProps {
  avatarUrl?: string;
  effectiveBalance: number | null;
  hasUnmeteredAccess: boolean;
  allowance?: TimeAllowance;
  billingEnabled?: boolean;
  size?: number;
  onPress?: () => void;
}

const AvatarRing: React.FC<AvatarRingProps> = ({
  avatarUrl,
  effectiveBalance,
  hasUnmeteredAccess,
  allowance,
  billingEnabled = true,
  size = 110,
  onPress,
}) => {
  const ringState: RingState = billingEnabled
    ? getRingState(effectiveBalance, hasUnmeteredAccess)
    : 'good';
  const config = RING_CONFIG[ringState];

  // Depletion percentage (static for non-critical).
  const progressPct = useMemo(() => {
    if (!allowance || ringState === 'good') return 100;
    const remaining = (allowance.amount - allowance.used) / allowance.amount;
    return Math.max(0, Math.min(100, remaining * 100));
  }, [allowance, ringState]);

  const radius = size / 2 - config.strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progressPct / 100) * circumference;

  // Critical countdown duration — match RN: min(remaining * 1000, 60_000).
  const countdownMs = useMemo(() => {
    if (
      ringState !== 'critical' ||
      effectiveBalance === null ||
      effectiveBalance <= 0
    ) {
      return 0;
    }
    return Math.min(effectiveBalance * 1000, 60_000);
  }, [ringState, effectiveBalance]);

  // We toggle a class to (re)start the countdown CSS animation when the
  // duration changes, by mounting the SVG circle with a unique key.
  return (
    <button
      type="button"
      className={`wg-avatarring wg-avatarring--${ringState}`}
      style={{ width: size, height: size }}
      onClick={onPress}
      aria-label="Account"
    >
      {/* Glow */}
      <span
        className="wg-avatarring__glow"
        style={{
          width: size + config.glowSize * 2,
          height: size + config.glowSize * 2,
          background: config.glowColor,
        }}
      />

      {/* Ring SVG */}
      <svg
        className="wg-avatarring__ring"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(0,0,0,0.3)"
          strokeWidth={config.strokeWidth}
          fill="none"
        />
        {ringState === 'critical' ? (
          <circle
            key={`critical-${countdownMs}`}
            className="wg-avatarring__countdown"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={config.color}
            strokeWidth={config.strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={0}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={
              {
                ['--wg-circumference' as any]: circumference,
                ['--wg-countdown-ms' as any]: `${countdownMs}ms`,
              } as React.CSSProperties
            }
          />
        ) : (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={config.color}
            strokeWidth={config.strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>

      {/* Avatar */}
      <span
        className="wg-avatarring__img-wrap"
        style={{ width: size, height: size }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="wg-avatarring__img" />
        ) : (
          <span className="wg-avatarring__placeholder">
            <PersonIcon size={Math.round(size * 0.6)} />
          </span>
        )}
      </span>
    </button>
  );
};

function PersonIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="rgba(255,255,255,0.6)"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="9" r="4" />
      <path d="M4 21c1.4-4 4.4-6 8-6s6.6 2 8 6" />
    </svg>
  );
}

export default AvatarRing;
